'use client';
import { useAnalyticsSkuViewedContext } from '@repo/segment/Analytics/AnalyticsSkuViewed';
import { CtaType } from '@repo/segment/Analytics/types';
import { createHandleOnClick, getFinishType } from '@repo/segment/Analytics/utils';
import { LinkType, MonetizableEnum } from '@repo/segment/enums';
import { useGetCtaLink } from '@repo/segment/hooks/useGetCtaLink';
import { useShoppingFinishedId } from '@repo/segment/hooks/useShoppingFinishedId';
import { AnalyticsType } from '@repo/segment/types';
import {
  isPaidBankingRoundupPath,
  trackCtaClickAction,
} from '@repo/segment/utils/trackCtaClickAction';
import React, { PropsWithChildren, useMemo } from 'react';

export interface CtaLinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  cta: CtaType;
  analyticsPayload: Omit<AnalyticsType, 'monetizable'>;
  linkType: LinkType;
  monetizable: MonetizableEnum;
}

export function CtaLink({
  analyticsPayload,
  cta,
  children,
  linkType,
  monetizable,
  ...rest
}: PropsWithChildren<CtaLinkProps>): React.JSX.Element {
  const shoppingFinishedId = useShoppingFinishedId();

  const { onClick: onClickFromProps, ...linkRest } = rest;
  const { index } = analyticsPayload;
  const { fireShoppingFinishedEvent, firePartnerCtaClickEvent } = useAnalyticsSkuViewedContext();
  const ctaUrl = useGetCtaLink({
    href: cta?.link,
    type: linkType,
    monetizable: monetizable,
    elementPosition: index,
    shoppingFinishedId,
  });

  const finishType = useMemo(() => getFinishType(monetizable), [monetizable]);

  const handleOnClick = () => {
    if (monetizable === MonetizableEnum.yesAssumed) {
      firePartnerCtaClickEvent({
        destinationUrl: ctaUrl,
        ctaType: cta.type != null ? String(cta.type) : 'unknown',
        monetizable,
        overrideShoppingFinishedId: shoppingFinishedId,
      });
    }
    if (monetizable === MonetizableEnum.no) {
      const fireNonMonetizingSFEvent = createHandleOnClick({
        fireShoppingFinishedEvent,
        overrideShoppingFinishedId: shoppingFinishedId,
        destinationUrl: ctaUrl,
        monetizable,
        type: cta.type,
      });

      fireNonMonetizingSFEvent();
    }
  };

  const handleClick: React.MouseEventHandler<HTMLAnchorElement> = event => {
    handleOnClick();
    if (typeof window !== 'undefined' && isPaidBankingRoundupPath(window.location.pathname)) {
      trackCtaClickAction({
        entityName: cta.text,
        shouldFireShoppingFinished: monetizable === MonetizableEnum.no,
        shoppingFinishedId,
        ctaType: cta.type != null ? String(cta.type) : 'unknown',
      });
    }
    onClickFromProps?.(event);
  };

  return (
    <a
      data-testid="cta-link"
      onClick={handleClick}
      href={ctaUrl}
      rel={`noopener ${finishType === 'external_application' ? 'nofollow' : ''}`}
      target="_blank"
      {...linkRest}
    >
      {children}
    </a>
  );
}
